import { PickerItem, TextField, Picker } from "@react-spectrum/s2";
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldSchema } from '../../types';
import { REDACTED_DISPLAY, isRedacted } from '../../utils/secretFields';
// field.default is typed unknown — avoid a bare template-literal interpolation
// collapsing an unexpected object/array default to "[object Object]".
function formatDefault(value: unknown): string {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
}
export interface DynamicConfigFieldsProps {
    fields: FieldSchema[];
    models: string[];
    values: Record<string, string>;
    onFieldChange: (fieldName: string, value: string) => void;
    prefix: string;
    /** Set of field names the user has manually edited (for secret masking). */ dirtyFields?: ReadonlySet<string>;
    /** Callback when a field becomes dirty for the first time. */ onFieldDirty?: (fieldName: string) => void;
    /** Field names to render read-only (e.g. an OOTB configuration's platform-managed credentials, where only behavioral params stay editable). */ disabledFieldNames?: ReadonlySet<string>;
}
export const DynamicConfigFields: React.FC<DynamicConfigFieldsProps> = ({ fields, models, values, onFieldChange, prefix, dirtyFields, onFieldDirty, disabledFieldNames })=>{
    const { t } = useTranslation();
    const apiKeyField = fields.find((f)=>f.name === 'api_key');
    const modelField = fields.find((f)=>f.name === 'model');
    const credentialFields = fields.filter((f)=>f.group === 'credentials' && f.name !== 'api_key');
    const otherModelFields = fields.filter((f)=>f.group === 'model' && f.name !== 'model');
    const behavioralFields = fields.filter((f)=>f.group === 'behavioral');
    const handleFieldChange = useCallback((field: FieldSchema, value: string)=>{
        if (field.secret && onFieldDirty && !dirtyFields?.has(field.name)) {
            onFieldDirty(field.name);
        }
        onFieldChange(field.name, value);
    }, [
        onFieldChange,
        onFieldDirty,
        dirtyFields
    ]);
    const renderTextField = (field: FieldSchema)=>{
        const defaultHint = field.default != null ? t('siteContent.dynamicConfig.defaultHint', {
            value: formatDefault(field.default)
        }) : undefined;
        const rawValue = values[field.name] ?? '';
        const isDirty = dirtyFields?.has(field.name) ?? false;
        const showRedactedPlaceholder = field.secret && isRedacted(rawValue) && !isDirty;
        return (<TextField key={field.name} label={field.label} name={`${prefix}-${field.name}`} type={field.secret ? 'password' : undefined} value={showRedactedPlaceholder ? '' : rawValue} placeholder={showRedactedPlaceholder ? REDACTED_DISPLAY : undefined} onChange={(v)=>handleFieldChange(field, v)} isRequired={field.required} isDisabled={disabledFieldNames?.has(field.name)} description={defaultHint || undefined} styles={" Za171"}/>);
    };
    const renderModelField = (field: FieldSchema)=>{
        if (models.length === 0) return renderTextField(field);
        const currentModel = values.model || '';
        const isOther = values.model_is_other === 'true' || (currentModel !== '' && !models.includes(currentModel));
        const pickerSelectedKey = isOther ? '__other__' : currentModel || null;
        const isFieldDisabled = disabledFieldNames?.has(field.name);
        return (<React.Fragment key={field.name}>
        <Picker label={field.label} name={`${prefix}-model-select`} value={pickerSelectedKey} onChange={(key)=>{
            const k = String(key);
            if (k === '__other__') {
                onFieldChange('model_is_other', 'true');
                onFieldChange('model', '');
            } else {
                onFieldChange('model_is_other', 'false');
                onFieldChange('model', k);
            }
        }} isRequired={field.required} isDisabled={isFieldDisabled} description={field.default != null ? t('siteContent.dynamicConfig.defaultHint', {
            value: formatDefault(field.default)
        }) : undefined} styles={" Za171"}>
          <>
            {models.map((m)=>(<PickerItem id={m} key={m}>{m}</PickerItem>))}
            <PickerItem id="__other__">{t('siteContent.dynamicConfig.other')}</PickerItem>
          </>
        </Picker>
        {isOther && (<TextField label={t('siteContent.dynamicConfig.customModelName')} name={`${prefix}-model`} value={currentModel} onChange={(v)=>onFieldChange('model', v)} isRequired={field.required} isDisabled={isFieldDisabled} placeholder={t('siteContent.dynamicConfig.enterModelName')} styles={" Za171"}/>)}
      </React.Fragment>);
    };
    return (<>
      {apiKeyField && renderTextField(apiKeyField)}

      {modelField && renderModelField(modelField)}

      {otherModelFields.map(renderTextField)}

      {credentialFields.map(renderTextField)}

      {behavioralFields.length > 0 && (<div className={" sd171 Ul171 ql171"}>
          {behavioralFields.map((field)=>(<TextField key={field.name} label={field.label} name={`${prefix}-${field.name}`} value={values[field.name] ?? ''} onChange={(v)=>onFieldChange(field.name, v)} UNSAFE_style={{
            width: `${Math.floor(100 / behavioralFields.length)}%`
        }}/>))}
        </div>)}
    </>);
};

import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-5e0b399c76eda918cb77667dda2cfc6c612bf466b37823d5ec9a508b4cf9316d.css";